import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import MainLayout from '../components/MainLayout';
import { useShipmentsStore } from '../store/shipmentsStore';
import { useInventoryStore } from '../store/inventoryStore';
import { Link } from 'react-router-dom';
import usePageTitle from '../utils/usePageTitle';
import { useGlobalLoading } from '../hooks/useGlobalLoading';
import Loader from '../components/Loader';
import ConfirmModal from '../components/ConfirmModal';
import BuyerModal from '../components/BuyerModal';
import { useApiCall } from '../hooks/useApiCall';
import CreateShipmentStatusModal from '../components/CreateShipmentStatusModal';
import AddToShipmentModal from '../components/AddToShipmentModal';
import { useToast } from '../components/ToastContainer';
import { api } from '../api/api';
import ShipmentsTable from '../components/ShipmentsTable';
import ShipmentsFilters from '../components/ShipmentsFilters';

// Стилизованные компоненты
const PageContainer = styled.div`
  width: 100%;
  max-width: 100vw;
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 80px);
  box-sizing: border-box;
`;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
  flex-wrap: wrap;
  gap: 16px;
`;

const ControlsContainer = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
`;

const ActionButton = styled.button`
  padding: 8px 16px;
  border-radius: 6px;
  border: 1px solid #24BA7D;
  background-color: transparent;
  color: #24BA7D;
  font-family: 'Ubuntu', sans-serif;
  font-weight: 500;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  
  &:hover {
    background-color: #24BA7D;
    color: #FFFFFF;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  @media (max-width: 768px) {
    padding: 10px 16px;
    font-size: 14px;
  }
`;



const PageTitle = styled.h1`
  font-family: 'Ubuntu', sans-serif;
  font-weight: 500;
  font-size: 24px;
  color: #FFFFFF;
  margin: 0;
  
  @media (min-width: 768px) {
    font-size: 28px;
  }
  
  @media (min-width: 1024px) {
    font-size: 32px;
  }
`;

const SearchContainer = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 6px 12px;
  border: 2px solid #609F94;
  border-radius: 8px;
  margin-bottom: 20px;
  
  @media (min-width: 768px) {
    padding: 7px 14px;
    margin-bottom: 24px;
  }
  
  @media (min-width: 1024px) {
    padding: 8px 16px;
    margin-bottom: 32px;
  }
  
  @media (min-width: 1440px) {
    padding: 10px 20px;
    margin-bottom: 40px;
  }
  
  @media (min-width: 1920px) {
    padding: 12px 24px;
    margin-bottom: 48px;
  }
`;

const SearchInput = styled.input`
  background: transparent;
  border: none;
  outline: none;
  width: 100%;
  font-family: 'Ubuntu', sans-serif;
  font-size: 12px;
  color: #FFFFFF;
  
  @media (min-width: 768px) {
    font-size: 13px;
  }
  
  @media (min-width: 1024px) {
    font-size: 14px;
  }
  
  @media (min-width: 1440px) {
    font-size: 16px;
  }
  
  @media (min-width: 1920px) {
    font-size: 18px;
  }
  
  &::placeholder {
    color: #609F94;
  }
`;

const CardsContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 100%;
  gap: 8px;
  flex: 1;
  overflow: visible;
  box-sizing: border-box;
  margin: 0;
  padding: 0;
`;


const FilterRow = styled.div`
  display: none;
  user-select: none;
  
  /* Планшеты малые (768px - 899px) */
  @media (min-width: 768px) {
    display: grid;
    grid-template-columns: 2fr 1.2fr 1fr 1fr;
    align-items: center;
    width: 100%;
    max-width: 100%;
    margin-bottom: 8px;
    column-gap: 6px;
    padding: 0 12px;
    box-sizing: border-box;
  }

  /* Планшеты средние (900px - 1023px) */
  @media (min-width: 900px) {
    grid-template-columns: 2.2fr 1.4fr 1.1fr 1.1fr;
    column-gap: 10px;
  }

  /* Ноутбуки малые (1024px - 1199px) */
  @media (min-width: 1024px) {
    grid-template-columns: 2.4fr 1.6fr 1.2fr 1.2fr;
    column-gap: 12px;
  }

  /* Ноутбуки средние (1200px - 1439px) */
  @media (min-width: 1200px) {
    grid-template-columns: 2.6fr 1.6fr 1.2fr 1.2fr;
    column-gap: 14px;
  }

  /* Десктопы средние (1440px - 1679px) */
  @media (min-width: 1440px) {
    grid-template-columns: 2.8fr 1.8fr 1.3fr 1.3fr;
    column-gap: 16px;
  }

  /* Десктопы большие (1680px - 1919px) */
  @media (min-width: 1680px) {
    grid-template-columns: 3fr 2fr 1.4fr 1.4fr;
    column-gap: 18px;
    padding: 0 20px;
  }

  /* Мониторы большие (1920px+) */
  @media (min-width: 1920px) {
    grid-template-columns: 3.2fr 2.2fr 1.5fr 1.5fr;
    column-gap: 20px;
    padding: 0 24px;
  }

  /* Мониторы сверхвысокого разрешения (2560px+) */
  @media (min-width: 2560px) {
    grid-template-columns: 3.5fr 2.4fr 1.6fr 1.6fr;
    column-gap: 24px;
    padding: 0 32px;
  }
`;

const FilterItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  width: 100%;
  gap: 2px;
  padding: 4px 8px;
  border-radius: 6px;
  transition: background-color 0.2s ease;
  user-select: none;

  &:hover {
    background-color: rgba(96, 159, 148, 0.08);
  }
  
  &:first-child {
    justify-content: flex-start;
    cursor: default;
    
    &:hover {
      background-color: transparent;
    }
  }
  
  &:last-child {
    cursor: default;
    
    &:hover {
      background-color: transparent;
    }
  }
`;

const FilterLabel = styled.span`
  font-family: 'Ubuntu', sans-serif;
  font-weight: 400;
  font-size: 12px;
  color: #609F94;
  white-space: nowrap;
  flex-shrink: 0;
  user-select: none;

  @media (min-width: 768px) {
    font-size: 13px;
  }

  @media (min-width: 1024px) {
    font-size: 14px;
  }

  @media (min-width: 1440px) {
    font-size: 16px;
  }

  @media (min-width: 1920px) {
    font-size: 18px;
  }
`;

const CardContainer = styled.div`
  display: flex;
  flex-direction: column;
  background-color: #1a2e2e;
  border-radius: 12px;
  width: 100%;
  min-height: 80px;
  padding: 0;
  box-sizing: border-box;
  overflow: visible;
  animation: fadeIn 0.3s ease-in-out;
  border: 1px solid #2c4a4a;
  margin: 0;

  @media (min-width: 768px) {
    min-height: 60px;
    border-radius: 12px;
  }

  @media (min-width: 1024px) {
    min-height: 64px;
    border-radius: 12px;
  }

  @media (min-width: 1440px) {
    min-height: 68px;
    border-radius: 12px;
  }

  @media (min-width: 1920px) {
    min-height: 72px;
    border-radius: 12px;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const CardWithExpandWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  width: 100%;

  @media (min-width: 480px) {
    gap: 10px;
  }

  @media (min-width: 768px) {
    gap: 12px;
  }

  @media (min-width: 1024px) {
    gap: 16px;
  }

  @media (min-width: 1440px) {
    gap: 20px;
  }
`;

const SeparateExpandButton = styled.button<{ $expanded: boolean }>`
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  transform: ${(props) =>
    props.$expanded ? "rotate(180deg)" : "rotate(0deg)"};
  transition: transform 0.3s ease;
  margin-top: 12px;
  border-radius: 50%;

  &:hover {
    background-color: rgba(96, 159, 148, 0.1);
  }

  &:active {
    background-color: rgba(96, 159, 148, 0.2);
  }

  svg {
    color: #609f94;
    width: 18px;
    height: 18px;
  }

  @media (min-width: 480px) {
    width: 44px;
    height: 44px;

    svg {
      width: 20px;
      height: 20px;
    }
  }

  @media (min-width: 768px) {
    width: 48px;
    height: 48px;
    margin-top: 14px;

    svg {
      width: 22px;
      height: 22px;
    }
  }

  @media (min-width: 1024px) {
    width: 52px;
    height: 52px;
    margin-top: 16px;

    svg {
      width: 24px;
      height: 24px;
    }
  }

  @media (min-width: 1440px) {
    width: 56px;
    height: 56px;
    margin-top: 20px;

    svg {
      width: 26px;
      height: 26px;
    }
  }

  @media (min-width: 1920px) {
    width: 60px;
    height: 60px;
    margin-top: 24px;

    svg {
      width: 28px;
      height: 28px;
    }
  }
`;

const ArrowDownIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M16.6 7.45834L11.1667 12.8917C10.525 13.5333 9.47497 13.5333 8.83331 12.8917L3.39998 7.45834"
      stroke="#A3A3A3"
      strokeWidth="1.5"
      strokeMiterlimit="10"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ExpandableContent = styled.div<{ $expanded: boolean }>`
  overflow: hidden;
  max-height: ${(props) => (props.$expanded ? "auto" : "0")};
  opacity: ${(props) => (props.$expanded ? "1" : "0")};
  transition: max-height 0.3s ease, opacity 0.3s ease;

  @media (max-width: 767px) {
    max-height: ${(props) => (props.$expanded ? "8000px" : "0")};
  }
`;

const SizeVariantContainer = styled.div`
  background: #1a2e2e;
  border-radius: 0;
  padding: 12px;
  border-top: 1px solid #2c4a4a;

  @media (min-width: 768px) {
    padding: 16px;
  }

  &:last-child {
    border-radius: 0 0 12px 12px;
  }

  &:only-child {
    border-radius: 0 0 12px 12px;
  }
`;

const SizeVariantContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 0 8px 8px;
  margin-top: 8px;

  @media (min-width: 768px) {
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-start;
    padding: 0 16px 8px;
    gap: 16px;
  }
`;

const SizeVariantInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
`;

const SizeVariantGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 8px;
  width: 100%;

  @media (min-width: 400px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: 600px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (min-width: 768px) {
    grid-template-columns: auto repeat(4, 1fr);
    align-items: center;
  }
`;

const InfoPill = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px 12px;
  background: rgba(8, 28, 33, 0.48);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
`;

const InfoLabel = styled.span`
  font-family: "Ubuntu", sans-serif;
  font-size: 11px;
  color: #609f94;
  font-weight: 400;

  @media (min-width: 768px) {
    font-size: 12px;
  }
`;

const InfoValue = styled.span`
  font-family: "Ubuntu", sans-serif;
  font-size: 13px;
  color: #ffffff;
  font-weight: 500;

  @media (min-width: 768px) {
    font-size: 14px;
  }
`;

const SizeVariantPrice = styled.span`
  font-family: "Ubuntu", sans-serif;
  font-size: 20px;
  font-weight: bold;
  color: #24ba7d;
  text-align: center;

  @media (min-width: 768px) {
    font-size: 20px;
  }

  @media (min-width: 1024px) {
    font-size: 20px;
  }

  @media (min-width: 1440px) {
    font-size: 20px;
  }

  @media (min-width: 1920px) {
    font-size: 20px;
  }
`;

const SizeVariantActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-shrink: 0;

  @media (max-width: 767px) {
    margin-top: 12px;
    border-top: 1px solid #2c4a4a;
    padding-top: 12px;
  }

  @media (min-width: 768px) {
    flex-direction: row;
  }
`;

const StatusButton = styled.button<{ $color: string }>`
  padding: 6px 16px;
  border-radius: 8px;
  border: 1px solid ${(props) => props.$color};
  background-color: transparent;
  color: ${(props) => props.$color};
  font-family: "Ubuntu", sans-serif;
  font-weight: 500;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background-color: ${(props) => props.$color};
    color: #0e1f1f;
    transform: translateY(-1px);
  }

  @media (max-width: 360px) {
    padding: 6px 12px;
    font-size: 12px;
  }
`;

const CardRow = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  align-items: center;
  width: 100%;
  min-height: 80px;
  column-gap: 8px;
  padding: 12px;
  overflow: visible;
  box-sizing: border-box;

  /* Планшеты малые (768px - 899px) */
  @media (min-width: 768px) {
    grid-template-columns: 2fr 1.2fr 1fr 1fr;
    column-gap: 6px;
    padding: 12px;
    min-height: 60px;
    max-width: 100%;
    box-sizing: border-box;
  }

  /* Планшеты средние (900px - 1023px) */
  @media (min-width: 900px) {
    grid-template-columns: 2.2fr 1.4fr 1.1fr 1.1fr;
    column-gap: 10px;
    min-height: 60px;
  }

  /* Ноутбуки малые (1024px - 1199px) */
  @media (min-width: 1024px) {
    grid-template-columns: 2.4fr 1.6fr 1.2fr 1.2fr;
    column-gap: 12px;
    min-height: 64px;
  }

  /* Ноутбуки средние (1200px - 1439px) */
  @media (min-width: 1200px) {
    grid-template-columns: 2.6fr 1.6fr 1.2fr 1.2fr;
    column-gap: 14px;
    min-height: 64px;
  }

  /* Десктопы средние (1440px - 1679px) */
  @media (min-width: 1440px) {
    grid-template-columns: 2.8fr 1.8fr 1.3fr 1.3fr;
    column-gap: 16px;
    min-height: 68px;
  }

  /* Десктопы большие (1680px - 1919px) */
  @media (min-width: 1680px) {
    grid-template-columns: 3fr 2fr 1.4fr 1.4fr;
    column-gap: 18px;
    padding: 12px 20px;
    min-height: 68px;
  }

  /* Мониторы большие (1920px+) */
  @media (min-width: 1920px) {
    grid-template-columns: 3.2fr 2.2fr 1.5fr 1.5fr;
    column-gap: 20px;
    padding: 12px 24px;
    min-height: 72px;
  }

  /* Мониторы сверхвысокого разрешения (2560px+) */
  @media (min-width: 2560px) {
    grid-template-columns: 3.5fr 2.4fr 1.6fr 1.6fr;
    column-gap: 24px;
    padding: 12px 32px;
    min-height: 76px;
  }
`;

const ProductInfo = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex: 1;
  min-width: 0;
  overflow: hidden;
`;

const ProductImage = styled.div`
  width: 50px;
  height: 50px;
  background-color: transparent;
  border-radius: 6px;
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
  flex-shrink: 0;
  
  @media (min-width: 768px) {
    width: 70px;
    height: 70px;
    border-radius: 7px;
  }
  
  @media (min-width: 1024px) {
    width: 80px;
    height: 80px;
    border-radius: 8px;
  }
  
  @media (min-width: 1440px) {
    width: 96px;
    height: 96px;
    border-radius: 10px;
  }
  
  @media (min-width: 1920px) {
    width: 112px;
    height: 112px;
    border-radius: 12px;
  }
`;

const ProductDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  justify-content: center;
  flex: 1;
  min-width: 0;
  overflow: hidden;
`;

const ProductName = styled.h3`
  font-family: 'Ubuntu', sans-serif;
  font-weight: 500;
  font-size: 12px;
  margin: 0;
  color: #FFFFFF;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  word-break: break-word;

  @media (min-width: 768px) {
    font-size: 12px;
    -webkit-line-clamp: 1;
  }

  @media (min-width: 900px) {
    font-size: 13px;
  }

  @media (min-width: 1024px) {
    font-size: 14px;
  }

  @media (min-width: 1200px) {
    font-size: 15px;
  }

  @media (min-width: 1440px) {
    font-size: 16px;
  }

  @media (min-width: 1680px) {
    font-size: 17px;
  }

  @media (min-width: 1920px) {
    font-size: 18px;
  }

  @media (min-width: 2560px) {
    font-size: 20px;
  }
`;

const ProductModel = styled.span`
  font-family: 'Ubuntu', sans-serif;
  font-weight: 500;
  font-size: 12px;
  color: #609F94;
  white-space: nowrap;

  @media (min-width: 768px) {
    font-size: 12px;
  }

  @media (min-width: 900px) {
    font-size: 13px;
  }

  @media (min-width: 1024px) {
    font-size: 14px;
  }

  @media (min-width: 1200px) {
    font-size: 15px;
  }

  @media (min-width: 1440px) {
    font-size: 16px;
  }

  @media (min-width: 1680px) {
    font-size: 17px;
  }

  @media (min-width: 1920px) {
    font-size: 18px;
  }

  @media (min-width: 2560px) {
    font-size: 20px;
  }
`;

const ProductColor = styled.span`
  font-family: 'Ubuntu', sans-serif;
  font-weight: 400;
  font-size: 12px;
  color: #838D90;
  white-space: nowrap;

  @media (min-width: 768px) {
    font-size: 11px;
  }

  @media (min-width: 900px) {
    font-size: 12px;
  }

  @media (min-width: 1024px) {
    font-size: 13px;
  }

  @media (min-width: 1200px) {
    font-size: 13px;
  }

  @media (min-width: 1440px) {
    font-size: 14px;
  }

  @media (min-width: 1680px) {
    font-size: 15px;
  }

  @media (min-width: 1920px) {
    font-size: 16px;
  }

  @media (min-width: 2560px) {
    font-size: 18px;
  }
`;

const SizeLabel = styled.span`
  font-family: "Ubuntu", sans-serif;
  font-weight: 500;
  font-size: 12px;
  color: #ffffff;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-word;

  @media (min-width: 768px) {
    font-size: 11px;
  }

  @media (min-width: 900px) {
    font-size: 12px;
  }

  @media (min-width: 1024px) {
    font-size: 13px;
  }

  @media (min-width: 1200px) {
    font-size: 14px;
  }

  @media (min-width: 1440px) {
    font-size: 15px;
  }

  @media (min-width: 1680px) {
    font-size: 16px;
  }

  @media (min-width: 1920px) {
    font-size: 17px;
  }

  @media (min-width: 2560px) {
    font-size: 19px;
  }
`;

const PriceLabel = styled.span`
  font-family: "Ubuntu", sans-serif;
  font-weight: 500;
  font-size: 12px;
  color: #24ba7d;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  text-align: center;
  letter-spacing: 0.5px;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-word;

  @media (min-width: 768px) {
    font-size: 12px;
  }

  @media (min-width: 900px) {
    font-size: 13px;
  }

  @media (min-width: 1024px) {
    font-size: 14px;
  }

  @media (min-width: 1200px) {
    font-size: 15px;
  }

  @media (min-width: 1440px) {
    font-size: 16px;
  }

  @media (min-width: 1680px) {
    font-size: 17px;
  }

  @media (min-width: 1920px) {
    font-size: 18px;
  }

  @media (min-width: 2560px) {
    font-size: 20px;
  }
`;

const ArticleLabel = styled.span`
  font-family: "Ubuntu", sans-serif;
  font-weight: 500;
  font-size: 12px;
  color: #ffffff;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-word;

  @media (min-width: 768px) {
    font-size: 11px;
  }

  @media (min-width: 900px) {
    font-size: 12px;
  }

  @media (min-width: 1024px) {
    font-size: 13px;
  }

  @media (min-width: 1200px) {
    font-size: 14px;
  }

  @media (min-width: 1440px) {
    font-size: 15px;
  }

  @media (min-width: 1680px) {
    font-size: 16px;
  }

  @media (min-width: 1920px) {
    font-size: 17px;
  }

  @media (min-width: 2560px) {
    font-size: 19px;
  }
`;

const MobileProductCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  
  @media (min-width: 768px) {
    display: none;
  }
`;

const MobileProductHeader = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;
`;

const MobileProductInfo = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
`;

const MobileProductPrice = styled.div`
  color: #24BA7D;
  font-weight: 500;
  font-size: 14px;
`;

const MobileProductArticle = styled.div`
  color: #609F94;
  font-size: 12px;
`;

const DesktopContent = styled.div`
  display: none;
  
  @media (min-width: 768px) {
    display: contents;
  }
`;

const SizesContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 3px;
  max-width: 100%;
  overflow: visible;
  align-items: center;
  justify-content: center;

  @media (max-width: 767px) {
    justify-content: flex-start;
  }
`;

const SizeChip = styled.span`
  background: #2c4a4a;
  color: #ffffff;
  padding: 2px 4px;
  border-radius: 3px;
  font-size: 9px;
  font-weight: 500;
  white-space: nowrap;
  flex-shrink: 0;

  @media (min-width: 768px) {
    padding: 2px 4px;
    font-size: 8px;
  }

  @media (min-width: 900px) {
    padding: 2px 4px;
    font-size: 9px;
  }

  @media (min-width: 1024px) {
    padding: 2px 5px;
    font-size: 10px;
  }

  @media (min-width: 1200px) {
    padding: 3px 5px;
    font-size: 10px;
  }

  @media (min-width: 1440px) {
    padding: 3px 6px;
    font-size: 11px;
    border-radius: 4px;
  }

  @media (min-width: 1680px) {
    padding: 3px 7px;
    font-size: 11px;
  }

  @media (min-width: 1920px) {
    padding: 4px 8px;
    font-size: 12px;
    border-radius: 5px;
  }

  @media (min-width: 2560px) {
    padding: 5px 10px;
    font-size: 14px;
    border-radius: 6px;
  }
`;

const SizeCount = styled.span`
  background: rgba(36, 186, 125, 0.2);
  color: #24ba7d;
  border: 1px solid rgba(36, 186, 125, 0.4);
  padding: 2px 6px;
  border-radius: 8px;
  font-size: 9px;
  font-weight: 600;
  font-family: "Ubuntu", sans-serif;
  margin-left: 4px;
  white-space: nowrap;
  flex-shrink: 0;

  @media (min-width: 768px) {
    padding: 2px 6px;
    font-size: 9px;
    border-radius: 8px;
  }

  @media (min-width: 1024px) {
    padding: 3px 7px;
    font-size: 10px;
    border-radius: 9px;
  }

  @media (min-width: 1200px) {
    padding: 3px 7px;
    font-size: 10px;
    border-radius: 9px;
  }

  @media (min-width: 1440px) {
    padding: 3px 8px;
    font-size: 11px;
    border-radius: 10px;
    margin-left: 6px;
  }

  @media (min-width: 1680px) {
    padding: 3px 8px;
    font-size: 11px;
    border-radius: 10px;
    margin-left: 6px;
  }

  @media (min-width: 1920px) {
    padding: 4px 10px;
    font-size: 12px;
    border-radius: 11px;
    margin-left: 8px;
  }

  @media (min-width: 2560px) {
    padding: 5px 12px;
    font-size: 14px;
    border-radius: 12px;
    margin-left: 10px;
  }
`;



const TrashIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M14.7485 5.99299C15.0927 6.01533 15.354 6.31202 15.332 6.65624L14.7908 15.0482L14.79 15.0514C14.7677 15.3691 14.7442 15.7205 14.6785 16.0467C14.6115 16.3793 14.4936 16.7306 14.2545 17.042C13.7535 17.6942 12.9001 17.9583 11.6747 17.9583H6.32481C5.09956 17.9583 4.24682 17.6941 3.74587 17.042C3.50666 16.7305 3.38887 16.3793 3.32188 16.0467C3.25618 15.7205 3.23183 15.3692 3.20957 15.0514V15.0482L2.66839 6.65624C2.64637 6.31193 2.90755 6.01522 3.25189 5.99299C3.5961 5.97109 3.89292 6.23222 3.91514 6.57649L4.45713 14.9643L4.49294 15.4209C4.50671 15.5588 4.52405 15.6839 4.54746 15.8001C4.59292 16.0257 4.65565 16.1741 4.73708 16.2803C4.87781 16.4634 5.22573 16.7083 6.32481 16.7083H11.6747C12.774 16.7083 13.1218 16.4634 13.2625 16.2803C13.344 16.1741 13.4074 16.0258 13.4529 15.8001C13.4997 15.5677 13.5197 15.3008 13.5432 14.9643L14.0844 6.57649C14.1066 6.2321 14.4041 5.97087 14.7485 5.99299Z" fill="#E47171" />
    <path d="M10.4467 12.1282C10.7619 12.1602 11.0082 12.4263 11.0082 12.75C11.0082 13.0736 10.7619 13.3398 10.4467 13.3717L10.3832 13.375H7.60817C7.263 13.375 6.98317 13.0952 6.98317 12.75C6.98317 12.4048 7.263 12.125 7.60817 12.125H10.3832L10.4467 12.1282Z" fill="#E47171" />
    <path d="M11.0831 8.79166C11.4282 8.79178 11.7081 9.07155 11.7081 9.41666C11.7081 9.76176 11.4282 10.0415 11.0831 10.0417H6.91644C6.57126 10.0417 6.29144 9.76183 6.29144 9.41666C6.29144 9.07148 6.57126 8.79166 6.91644 8.79166H11.0831Z" fill="#E47171" />
    <path d="M1.75 4.75C1.40469 4.75 1.125 4.47031 1.125 4.125C1.125 3.77969 1.40469 3.5 1.75 3.5H3.74951C4.51636 3.5 5.17041 2.93604 5.27759 2.17676L5.42578 1.20361C5.74121 -0.677734 7.38623 -2 9.39111 -2H8.60889C10.6138 -2 12.2588 -0.677734 12.5742 1.20361L12.7224 2.17676C12.8296 2.93604 13.4836 3.5 14.2505 3.5H16.25C16.5953 3.5 16.875 3.77969 16.875 4.125C16.875 4.47031 16.5953 4.75 16.25 4.75H1.75Z" fill="#E47171" />
  </svg>
);



const ProductLink = styled(Link)`
  font-family: 'Ubuntu', sans-serif;
  font-weight: 400;
  font-size: 14px;
  color: #609F94;
  cursor: pointer;
  text-decoration: none;
  border-bottom: 1px solid #609F94;
  padding-bottom: 1px;
  display: inline-block;
  
  &:hover {
    border-bottom-color: #24BA7D;
    color: #24BA7D;
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  margin-top: 24px;
  margin-bottom: 24px;
  padding: 0 12px;
  gap: 8px;
  flex-wrap: wrap;
  
  @media (min-width: 768px) {
    padding: 0 16px;
  }
`;

const PageButton = styled.button<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background-color: ${props => props.$active ? 'rgba(36, 186, 125, 0.16)' : 'rgba(8, 28, 33, 0.48)'};
  border: 1px solid ${props => props.$active ? '#24BA7D' : 'rgba(255, 255, 255, 0.16)'};
  color: ${props => props.$active ? '#24BA7D' : '#FFFFFF'};
  font-family: 'Ubuntu', sans-serif;
  font-weight: ${props => props.$active ? '600' : '500'};
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: ${props => props.$active ? 'rgba(36, 186, 125, 0.24)' : 'rgba(8, 28, 33, 0.64)'};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PaginationInfo = styled.div`
  font-family: 'Ubuntu', sans-serif;
  font-weight: 400;
  font-size: 12px;
  color: #609F94;
  margin-right: 0;
  text-align: center;
  flex: 1;
  min-width: 200px;
  
  @media (min-width: 768px) {
    font-size: 14px;
    margin-right: auto;
    flex: none;
    min-width: auto;
  }
`;

const ItemsPerPageContainer = styled.div`
  display: flex;
  align-items: center;
  margin-left: 0;
  
  @media (min-width: 768px) {
    margin-left: 16px;
  }
`;

const ItemsPerPageLabel = styled.span`
  font-family: 'Ubuntu', sans-serif;
  font-weight: 400;
  font-size: 14px;
  color: #609F94;
  margin-right: 8px;
`;

const ItemsPerPageSelect = styled.select`
  background-color: rgba(8, 28, 33, 0.48);
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 8px;
  color: #FFFFFF;
  font-family: 'Ubuntu', sans-serif;
  font-weight: 400;
  font-size: 14px;
  padding: 4px 8px;
  cursor: pointer;
  transition: opacity 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: rgba(36, 186, 125, 0.48);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  option {
    background-color: #172A2A;
  }
`;

const ActionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  justify-content: center;
  align-items: center;
  width: 100%;
  padding: 2px;
  
  @media (max-width: 767px) {
    flex-direction: column;
    gap: 4px;
    padding: 4px;
  }
  
  @media (min-width: 768px) {
    gap: 2px;
    padding: 2px;
  }
  
  @media (min-width: 1024px) {
    gap: 3px;
    padding: 3px;
  }
  
  @media (min-width: 1440px) {
    gap: 3px;
    padding: 3px;
  }
`;

// Функция для группировки размеров
const groupSizesByValue = (sizes: any[]) => {
  // Защита от некорректных данных
  if (!Array.isArray(sizes)) {
    return [];
  }
  
  const groupedSizes = new Map<string, { size: string; count: number; firstItem: any }>();
  
  sizes.forEach(size => {
    // Дополнительная защита от некорректных объектов размера
    if (!size || !size.size) {
      return;
    }
    
    const sizeValue = size.size.replace(' US', '');
    if (groupedSizes.has(sizeValue)) {
      const existing = groupedSizes.get(sizeValue)!;
      existing.count += 1;
    } else {
      groupedSizes.set(sizeValue, {
        size: sizeValue,
        count: 1,
        firstItem: size
      });
    }
  });

  return Array.from(groupedSizes.values()).sort((a, b) => {
    const aNum = parseFloat(a.size);
    const bNum = parseFloat(b.size);
    return aNum - bNum;
  });
};

// Функции форматирования
const formatPrice = (price: string): string => {
  if (!price) return '—';
  
  const cleanPrice = price.replace(/[^\d\-\s]/g, '');
  
  if (cleanPrice.includes('-')) {
    const parts = cleanPrice.split('-').map(p => p.trim());
    if (parts.length === 2 && parts[0] && parts[1]) {
      const min = parseInt(parts[0]).toLocaleString('ru-RU');
      const max = parseInt(parts[1]).toLocaleString('ru-RU');
      return `${min} - ${max} ₽`;
    }
  }
  
  const numericPrice = parseInt(cleanPrice.replace(/\s/g, ''));
  if (!isNaN(numericPrice)) {
    return `${numericPrice.toLocaleString('ru-RU')} ₽`;
  }
  
  return price;
};

const formatMarketPrice = (price: string): string => {
  if (!price || price === '—' || price === '-') return '—';
  
  const cleanPrice = price.replace(/[^\d.,]/g, '');
  
  if (!cleanPrice) return '—';
  
  const numericPrice = parseFloat(cleanPrice.replace(',', '.'));
  
  if (isNaN(numericPrice)) return '—';
  
  return `${numericPrice} ₽`;
};

// Иконки
const SortIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 4px;
  flex-shrink: 0;
  user-select: none;
  
  svg {
    width: 20px;
    height: 20px;
    
    @media (min-width: 768px) {
      width: 22px;
      height: 22px;
    }
    
    @media (min-width: 1024px) {
      width: 24px;
      height: 24px;
    }
    
    @media (min-width: 1440px) {
      width: 26px;
      height: 26px;
    }
    
    @media (min-width: 1920px) {
      width: 28px;
      height: 28px;
    }
  }
`;

const SortIcon = ({ direction }: { direction?: 'up' | 'down' | null }) => (
  direction && (
    <SortIconWrapper>
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d={direction === 'up'
            ? "M7.54 6.1L10 3.64L12.46 6.1"
            : "M12.46 13.88L10 16.34L7.54 13.88"}
          stroke="#609F94"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </SortIconWrapper>
  )
);


const ArrowLeftDoubleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M7.2 12.4333L3.96667 9.2C3.43334 8.66667 3.43334 7.78 3.96667 7.24667L7.2 4.01334" stroke="currentColor" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M12.0333 12.4333L8.79999 9.2C8.26666 8.66667 8.26666 7.78 8.79999 7.24667L12.0333 4.01334" stroke="currentColor" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ArrowRightDoubleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M8.96667 12.4333L12.2 9.2C12.7333 8.66667 12.7333 7.78 12.2 7.24667L8.96667 4.01334" stroke="currentColor" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M3.96667 12.4333L7.20001 9.2C7.73334 8.66667 7.73334 7.78 7.20001 7.24667L3.96667 4.01334" stroke="currentColor" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ArrowLeftIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M10.0334 4.01334L6.80002 7.24667C6.26669 7.78 6.26669 8.66667 6.80002 9.2L10.0334 12.4333" stroke="currentColor" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ArrowRightIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M5.96667 12.4333L9.20001 9.2C9.73334 8.66667 9.73334 7.78 9.20001 7.24667L5.96667 4.01334" stroke="currentColor" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Компонент страницы отгрузок
const Shipments: React.FC = () => {
  usePageTitle('Отгрузки');

  const {
    shipmentsItems,
    shipmentStatuses,
    isLoading,
    isLoadingStatuses,
    searchQuery,
    currentPage,
    itemsPerPage,
    totalItems,
    isInitialLoading,
    fetchShipmentStatuses,
    fetchShipmentsItems,
    createShipmentStatus,
    addItemToShipment,
    removeItemFromShipment,
    setSearchQuery,
    setCurrentPage,
    setItemsPerPage
  } = useShipmentsStore();

  const { addLoadingStep, removeLoadingStep } = useGlobalLoading();
  const { executeApiCall } = useApiCall();
  const { showSuccess, showError } = useToast();
  
  // Функция для отметки товара как проданного из inventoryStore
  const { markAsSold } = useInventoryStore();

  // Туториал
  // Состояния
  const [sorting, setSorting] = useState<{ id: string; desc: boolean }[]>([]);
  const [expandedCards, setExpandedCards] = useState<string[]>([]);

  // Модальные окна
  const [createStatusModal, setCreateStatusModal] = useState(false);
  const [addToShipmentModal, setAddToShipmentModal] = useState<{
    isOpen: boolean;
    product: any;
  }>({
    isOpen: false,
    product: null
  });

  // Состояние для модального окна подтверждения
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => { }
  });

  // Состояние для модального окна покупателя
  const [buyerModal, setBuyerModal] = useState<{
    isOpen: boolean;
    itemIds: number[];
    isLoading: boolean;
  }>({
    isOpen: false,
    itemIds: [],
    isLoading: false,
  });

  // Автозапуск туториала при переходе из инвентаря
  // Загрузка данных при инициализации
  useEffect(() => {
    useShipmentsStore.getState().fetchShipmentStatuses();
    useShipmentsStore.getState().fetchShipmentsItems({
      page: 0,
      limit: itemsPerPage
    });
  }, []);

  // Загрузка товаров с дебаунсом для поиска
  useEffect(() => {
    if (searchQuery === '') return; // Не делаем ничего если поиск пустой
    
    const timeoutId = setTimeout(() => {
      setCurrentPage(1); // Сброс на первую страницу при поиске
      useShipmentsStore.getState().fetchShipmentsItems({
        page: 0,
        limit: itemsPerPage
      });
    }, 500);
    
    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  // Загрузка товаров при смене страницы или лимита
  useEffect(() => {
    if (searchQuery !== '') return; // Не загружаем если идёт поиск
    
    useShipmentsStore.getState().fetchShipmentsItems({
      page: currentPage - 1,
      limit: itemsPerPage
    });
  }, [currentPage, itemsPerPage]);

  // Сортировка (на данных от API)
  const sortedItems = useMemo(() => {
    if (sorting.length === 0) return shipmentsItems;

    const sortState = sorting[0];
    const sortedData = [...shipmentsItems];

    sortedData.sort((a, b) => {
      if (sortState.id === 'size') {
        const aSizes = Array.isArray(a.sizes) ? a.sizes : [];
        const bSizes = Array.isArray(b.sizes) ? b.sizes : [];
        const aSize = aSizes.length > 0 ? Math.min(...aSizes.map((s: any) => parseFloat(s.size.replace(/[^\d.]/g, '')))) : 0;
        const bSize = bSizes.length > 0 ? Math.min(...bSizes.map((s: any) => parseFloat(s.size.replace(/[^\d.]/g, '')))) : 0;
        return sortState.desc ? bSize - aSize : aSize - bSize;
      } else if (sortState.id === 'price') {
        const aSizes = Array.isArray(a.sizes) ? a.sizes : [];
        const bSizes = Array.isArray(b.sizes) ? b.sizes : [];
        const aPrice = aSizes.length > 0 ? Math.min(...aSizes.map((s: any) => parseFloat(s.price.replace(/[^\d]/g, '')))) : 0;
        const bPrice = bSizes.length > 0 ? Math.min(...bSizes.map((s: any) => parseFloat(s.price.replace(/[^\d]/g, '')))) : 0;
        return sortState.desc ? bPrice - aPrice : aPrice - bPrice;
      }
      return 0;
    });

    return sortedData;
  }, [shipmentsItems, sorting]);

  // Обработчики модальных окон
  const handleCreateStatus = async (status: string) => {
    await executeApiCall(
      () => createShipmentStatus(status),
      { successMessage: 'Статус успешно создан' }
    );
  };

  const handleAddToShipment = async (sneakerId: number, statusId: number) => {
    await executeApiCall(
      () => addItemToShipment(sneakerId, statusId),
      { successMessage: 'Товар добавлен в отгрузку' }
    );
  };

  const handleRemoveFromShipment = async (sneakerId: number) => {
    await executeApiCall(
      () => removeItemFromShipment(sneakerId),
      { successMessage: 'Товар удален из отгрузки' }
    );
  };


  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const currentItems = sortedItems;

  // Управление глобальной загрузкой
  useEffect(() => {
    if (isLoading && shipmentsItems.length === 0) {
      addLoadingStep('shipments-loading', 'Загружаем отгрузки...', 5);
    } else {
      removeLoadingStep('shipments-loading');
    }
  }, [isLoading, shipmentsItems.length, addLoadingStep, removeLoadingStep]);

  // Обработчики
  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
    setExpandedCards([]);
  };

  // Обработчик разворачивания/сворачивания карточки
  const toggleExpand = (article: string) => {
    setExpandedCards((prev) =>
      prev.includes(article)
        ? prev.filter((cardArticle) => cardArticle !== article)
        : [...prev, article]
    );
  };

  // Обработчик продажи конкретного размера
  const handleSoldSize = (sizeId: number) => {
    setBuyerModal({
      isOpen: true,
      itemIds: [sizeId],
      isLoading: false,
    });
  };

  // Обработчик возврата конкретного размера в инвентарь
  const handleReturnToInventorySize = async (sizeId: number) => {
    setConfirmModal({
      isOpen: true,
      title: "Возврат в инвентарь",
      message: "Вы уверены, что хотите вернуть этот размер в инвентарь?",
      onConfirm: async () => {
        try {
          addLoadingStep('return-to-inventory', 'Возвращаем товар в инвентарь...');
          await api.putInStore(sizeId);
          removeLoadingStep('return-to-inventory');

          // Закрываем модальное окно
          setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: () => {} });

          // Обновляем данные
          await fetchShipmentsItems();

          showSuccess('Товар успешно возвращен в инвентарь');
        } catch (error: any) {
          removeLoadingStep('return-to-inventory');
          showError('Ошибка', error.message || 'Не удалось вернуть товар в инвентарь');
        }
      }
    });
  };

  // Получение текущих параметров фильтрации для отгрузок
  const getCurrentFilterParams = () => {
    const { selectedBrandId, selectedModelId, selectedArticleId, selectedSize, priceFrom, priceTo, isSale } = useInventoryStore.getState();
    return {
      selectedBrandId,
      selectedModelId,
      selectedArticleId,
      selectedSize,
      priceFrom,
      priceTo,
      isSale
    };
  };

  // Обработчик изменения количества элементов на странице
  const handleItemsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newItemsPerPage = Number(e.target.value);
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Сброс на первую страницу при изменении лимита
  };

  // Обработчик добавления товара в отгрузку
  const handleAddToShipmentClick = (product: any) => {
    setAddToShipmentModal({
      isOpen: true,
      product: {
        id: product.id,
        name: product.name,
        model: product.model,
        size: product.size,
        imageUrl: product.imageUrl
      }
    });
  };

  // Обработчик подтверждения продажи с указанием покупателя
  const handleBuyerConfirm = async (buyer: string) => {
    if (!buyerModal.itemIds.length) return;

    setBuyerModal((prev) => ({ ...prev, isLoading: true }));

    try {
      // Отмечаем размер как проданный с указанием покупателя
      for (const itemId of buyerModal.itemIds) {
        await executeApiCall(() => markAsSold(itemId, buyer), {
          successMessage: 'Товар успешно отмечен как проданный',
        });
      }

      // Перезагружаем данные
      await fetchShipmentsItems();

      // Закрываем модальное окно
      setBuyerModal({
        isOpen: false,
        itemIds: [],
        isLoading: false,
      });
    } catch (error) {
      setBuyerModal((prev) => ({ ...prev, isLoading: false }));
    }
  };

  // Обработчик отмены ввода покупателя
  const handleBuyerCancel = () => {
    setBuyerModal({
      isOpen: false,
      itemIds: [],
      isLoading: false,
    });
  };


  const toggleSort = (field: 'size' | 'price') => {
    setSorting(prev => {
      const existingSort = prev.find(s => s.id === field);
      if (!existingSort) {
        return [{ id: field, desc: false }];
      } else if (!existingSort.desc) {
        return [{ id: field, desc: true }];
      } else {
        return [];
      }
    });
  };

  const getSortDirection = (field: string): 'up' | 'down' | null => {
    const sortState = sorting.find(s => s.id === field);
    if (!sortState) return null;
    return sortState.desc ? 'down' : 'up';
  };

  const renderPaginationButtons = () => {
    const buttons = [];
    const maxVisiblePages = 5;

    if (currentPage > 1) {
      buttons.push(
        <PageButton
          key="first"
          onClick={() => handlePageChange(1)}
          disabled={currentPage === 1 || isLoading}
        >
          <ArrowLeftDoubleIcon />
        </PageButton>
      );
    }

    if (currentPage > 1) {
      buttons.push(
        <PageButton
          key="prev"
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1 || isLoading}
        >
          <ArrowLeftIcon />
        </PageButton>
      );
    }

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      buttons.push(
        <PageButton
          key={i}
          $active={i === currentPage}
          onClick={() => handlePageChange(i)}
          disabled={isLoading}
        >
          {i}
        </PageButton>
      );
    }

    if (currentPage < totalPages) {
      buttons.push(
        <PageButton
          key="next"
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages || isLoading}
        >
          <ArrowRightIcon />
        </PageButton>
      );
    }

    if (currentPage < totalPages) {
      buttons.push(
        <PageButton
          key="last"
          onClick={() => handlePageChange(totalPages)}
          disabled={currentPage === totalPages || isLoading}
        >
          <ArrowRightDoubleIcon />
        </PageButton>
      );
    }

    return buttons;
  };

  if (isLoading && !shipmentsItems.length) {
    return (
      <MainLayout activePage="shipments">
        <div />
      </MainLayout>
    );
  }

  return (
    <MainLayout activePage="shipments">
      <PageContainer>
        <HeaderContainer data-tutorial="shipments-header">
          <PageTitle>Отгрузки</PageTitle>
          <ControlsContainer>
            <ActionButton 
              onClick={() => setCreateStatusModal(true)}
              disabled={isLoadingStatuses}
            >
              Создать статус
            </ActionButton>
          </ControlsContainer>
        </HeaderContainer>


        <SearchContainer data-tutorial="shipments-search">
          <SearchInput
            placeholder="Найти товар в отгрузке"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </SearchContainer>

        <ShipmentsFilters />

        {isInitialLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
            <Loader size="medium" text="Загружаем..." />
          </div>
        ) : (
          <ShipmentsTable
            data={sortedItems}
            isLoading={isLoading}
            onSoldSize={handleSoldSize}
            onReturnToInventory={handleReturnToInventorySize}
            formatPrice={formatPrice}
          />
        )}

        {shipmentsItems.length > 0 && totalItems > itemsPerPage && (
          <PaginationContainer>
            <PaginationInfo>
              {shipmentsItems.length === 0
                ? 'Нет элементов'
                : `Показано ${((currentPage - 1) * itemsPerPage) + 1}-${Math.min(currentPage * itemsPerPage, totalItems)} из ${totalItems}`
              }
            </PaginationInfo>
            {renderPaginationButtons()}
            <ItemsPerPageContainer>
              <ItemsPerPageLabel>Показывать по:</ItemsPerPageLabel>
              <ItemsPerPageSelect
                value={itemsPerPage}
                onChange={handleItemsPerPageChange}
                disabled={isLoading}
              >
                <option value="5">5</option>
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="50">50</option>
              </ItemsPerPageSelect>
            </ItemsPerPageContainer>
          </PaginationContainer>
        )}
      </PageContainer>

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
      />

      <BuyerModal
        isOpen={buyerModal.isOpen}
        onConfirm={handleBuyerConfirm}
        onCancel={handleBuyerCancel}
        isLoading={buyerModal.isLoading}
      />

      {/* Модальное окно для создания статуса */}
      <CreateShipmentStatusModal
        isOpen={createStatusModal}
        onClose={() => setCreateStatusModal(false)}
        onSuccess={() => {
          showSuccess('Статус успешно создан');
          fetchShipmentStatuses();
        }}
        onSubmit={handleCreateStatus}
      />

      {/* Модальное окно для добавления товара в отгрузку */}
      <AddToShipmentModal
        isOpen={addToShipmentModal.isOpen}
        product={addToShipmentModal.product}
        statuses={shipmentStatuses}
        onClose={() => setAddToShipmentModal({ isOpen: false, product: null })}
        onSuccess={() => {
          showSuccess('Товар добавлен в отгрузку');
          fetchShipmentsItems();
        }}
        onSubmit={handleAddToShipment}
      />
    </MainLayout>
  );
};

export default Shipments; 